import { useCallback, useEffect, useState } from 'react'
import 'react-date-range/dist/styles.css' // main style file
import 'react-date-range/dist/theme/default.css'
import { useDispatch, useSelector } from 'react-redux'
import HeaderMain from '../../components/header-main/HeaderMain'
import { fetchClients } from '../../redux/reducers/clients'
import {
  fetchUser,
  fetchUsers,
  setCurrentUser,
  setIsVisibleDeleteModalUser,
  setIsVisibleModalUser,
} from '../../redux/reducers/users'
import { clientsSelector } from '../../redux/selectors/clients-selector'
import { usersSelector } from '../../redux/selectors/users-selector'
import { AppDispatch } from '../../redux/store'

import classNames from 'classnames'
import DeleteButton from '../../components/buttons/actions-button/DeleteButton'
import EditButton from '../../components/buttons/actions-button/EditButton'
import Spinner from '../../components/spinner/Spinner'
import { User } from '../../redux/reducers/reports'
import styles from './Employees.module.css'

const roles: Record<string, string> = {
  driver: 'Водитель',
  admin: 'Админ',
  owner: 'Владелец',
  client: 'Клиент',
}

const FILTER_OPTIONS = [
  { value: 'all', label: 'Все юзеры' },
  { value: 'client', label: 'Клиенты' },
  { value: 'driver', label: 'Сотрудники' },
  { value: 'admin', label: 'Админы' },
] as const

type FilterRole = (typeof FILTER_OPTIONS)[number]['value']

function Employee() {
  const dispatch = useDispatch<AppDispatch>()
  const { users, isPendingGetUser } = useSelector(usersSelector)
  const { clients = [] } = useSelector(clientsSelector)
  const [sortField, setSortField] = useState<number | string | null>(null)
  const [sortOrder, setSortOrder] = useState('asc')
  const [sortNewUsers, setSortNewUsers] = useState<any[]>([])
  const [filterRole, setFilterRole] = useState<FilterRole>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false)
  const [phoneDropdownOpenId, setPhoneDropdownOpenId] = useState<number | null>(
    null
  )

  const handleEditUser = async (item: User) => {
    await dispatch(fetchUser(item.id))
    dispatch(setIsVisibleModalUser(true))
  }

  const handleDeleteUser = (item: User) => {
    dispatch(setCurrentUser(item))
    dispatch(setIsVisibleDeleteModalUser(true))
  }

  const togglePhoneDropdown = (id: number) => {
    setPhoneDropdownOpenId((prev) => (prev === id ? null : id))
  }

  const getPhones = useCallback((item: any): string[] => {
    const list: string[] = []
    if (item.phone) list.push(item.phone)
    if (item.phone2) list.push(item.phone2)
    return list
  }, [])

  const filterAndSortUsers = useCallback(() => {
    let list = [...users]

    if (filterRole !== 'all') {
      list = list.filter((u) => u.role === filterRole)
    }

    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase()
      list = list.filter((u) => {
        const name = (u.name || '').toLowerCase()
        const email = (u.email || u.login || '').toLowerCase()
        const company = (u as any).client?.entity_name?.toLowerCase() || ''
        return name.includes(q) || email.includes(q) || company.includes(q)
      })
    }

    if (sortField !== null) {
      list.sort((a, b) => {
        let valueA: any, valueB: any
        switch (sortField) {
          case 'ID':
            valueA = a.id
            valueB = b.id
            break
          case 'Сотрудник':
            valueA = a.name
            valueB = b.name
            break
          case 'E-mail':
            valueA = (a as any).login || a.email || ''
            valueB = (b as any).login || b.email || ''
            break
          case 'Статус':
            valueA = a.role
            valueB = b.role
            break
          case 'Доступ':
            valueA = a.active ? 1 : 0
            valueB = b.active ? 1 : 0
            break
          default:
            return 0
        }
        if (valueA < valueB) return sortOrder === 'asc' ? -1 : 1
        if (valueA > valueB) return sortOrder === 'asc' ? 1 : -1
        return 0
      })
    }

    setSortNewUsers(list)
  }, [users, filterRole, searchQuery, sortField, sortOrder])

  useEffect(() => {
    dispatch(fetchUsers())
  }, [dispatch])

  useEffect(() => {
    dispatch(fetchClients())
  }, [dispatch])

  useEffect(() => {
    filterAndSortUsers()
  }, [filterAndSortUsers])

  const sortReports = (field: string) => {
    const order = sortField === field && sortOrder === 'asc' ? 'desc' : 'asc'
    setSortField(field)
    setSortOrder(order)
  }

  const currentFilterLabel =
    FILTER_OPTIONS.find((o) => o.value === filterRole)?.label || 'Все юзеры'

  return (
    <div className={styles.accountContainer}>
      <div className={styles.accountWideContent}>
        <div className={styles.accountWideContentContainer}>
          <HeaderMain />

          <div className="row">
            <div className="col-12 offset-md-1 col-md-10 offset-lg-1 col-lg-10 offset-xl-1 col-xl-10">
              {/* FILTER */}
              <div className="row align-items-center justify-content-between mb-4">
                <div className="col-6 col-sm-5 col-md-auto pb-2 pr-1">
                  <div
                    className={classNames(
                      styles.dropdown,
                      styles.dropdownSelect,
                      'd-block',
                      filterDropdownOpen && styles.opened
                    )}
                    style={{ textAlign: 'left' }}
                  >
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => setFilterDropdownOpen((prev) => !prev)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ')
                          setFilterDropdownOpen((prev) => !prev)
                      }}
                      className={classNames(
                        styles.btn,
                        styles.btnSmall,
                        styles.btnWide,
                        styles.btnInput,
                        styles.btnDropdown
                      )}
                    >
                      {currentFilterLabel}
                    </div>
                    <div className={styles.dropdownContent}>
                      {FILTER_OPTIONS.map((opt) => (
                        <div key={opt.value}>
                          <input
                            type="radio"
                            name="filter_status"
                            id={`status_${opt.value}`}
                            value={opt.value}
                            checked={filterRole === opt.value}
                            onChange={() => {
                              setFilterRole(opt.value)
                              setFilterDropdownOpen(false)
                            }}
                          />
                          <label htmlFor={`status_${opt.value}`}>
                            {opt.label}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="col-6 col-sm-7 col-md pb-2 pl-1">
                  <input
                    style={{ width: '100%' }}
                    type="text"
                    placeholder="Найти пользователя..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>

              {/* STAFF LIST */}
              <div className={styles.tableBigWrapper}>
                {isPendingGetUser ? (
                  <Spinner />
                ) : (
                  <table
                    className={`${styles.tableBig} ${styles.small}`}
                    cellPadding="0"
                    cellSpacing="0"
                  >
                    <thead>
                      <tr className={styles.thRow}>
                        <th style={{ width: 10 }}>
                          <div
                            className={styles.tableSortCol}
                            onClick={() => sortReports('ID')}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) =>
                              (e.key === 'Enter' || e.key === ' ') &&
                              sortReports('ID')
                            }
                          >
                            ID
                          </div>
                        </th>
                        <th>
                          <div
                            className={styles.tableSortCol}
                            onClick={() => sortReports('Сотрудник')}
                            tabIndex={0}
                            onKeyDown={(e) =>
                              (e.key === 'Enter' || e.key === ' ') &&
                              sortReports('Сотрудник')
                            }
                          >
                            Сотрудник
                          </div>
                        </th>
                        <th>
                          <div
                            className={styles.tableSortCol}
                            onClick={() => sortReports('E-mail')}
                            tabIndex={0}
                            onKeyDown={(e) =>
                              (e.key === 'Enter' || e.key === ' ') &&
                              sortReports('E-mail')
                            }
                          >
                            E-mail
                          </div>
                        </th>
                        <th>Доступ</th>
                        <th>Статус</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {sortNewUsers?.map((item: any) => {
                        const { active, avatar, id, name, role } = item
                        const email = item.email || item.login || ''
                        const clientId =
                          item.client_id ??
                          (typeof item.client === 'object' &&
                          item.client != null
                            ? item.client.id
                            : typeof item.client === 'number'
                              ? item.client
                              : undefined)
                        const linkedClient =
                          clientId != null
                            ? (clients as any[]).find(
                                (c: any) => Number(c.id) === Number(clientId)
                              )
                            : null

                        console.log('clientId', clientId)
                        console.log('item', item)

                        const companyName =
                          (typeof item.client === 'object' &&
                          item.client != null
                            ? item.client.entity_name || item.client.name
                            : null) ||
                          linkedClient?.entity_name ||
                          linkedClient?.name ||
                          ''
                        const phones = getPhones(item)

                        return (
                          <tr
                            key={item.id}
                            className={classNames(
                              styles.bxShadow,
                              !active && styles.disactive
                            )}
                          >
                            <td className={styles.tdId}>{id}</td>
                            <td className={styles.tdMain}>
                              <a
                                className={styles.avatarLink}
                                onClick={() => handleEditUser(item)}
                              >
                                <span
                                  className={styles.avatarImg}
                                  style={{
                                    backgroundImage: `url(${
                                      avatar?.sm || 'img/users/no-avatar.png'
                                    })`,
                                  }}
                                />
                                <span className={styles.name}>
                                  {name}
                                  {companyName && (
                                    <>
                                      <br />
                                      <span
                                        className={classNames(
                                          styles.small2,
                                          styles.colorBlue
                                        )}
                                      >
                                        <i
                                          className={classNames(
                                            styles.icon,
                                            styles.iconFlag,
                                            styles.filterBlue,
                                            styles.icon15
                                          )}
                                        />{' '}
                                        {companyName}
                                      </span>
                                    </>
                                  )}
                                </span>
                              </a>
                            </td>
                            <td className={styles.tdEmail}>{email || '-'}</td>
                            <td className={styles.tdAccess}>
                              {roles[role] || role}
                            </td>
                            {active ? (
                              <td className={styles.tdStatus}>
                                <i
                                  className={classNames(
                                    styles.icon,
                                    styles.iconVerified22,
                                    styles.icon16
                                  )}
                                />{' '}
                                Активен
                              </td>
                            ) : (
                              <td
                                className={classNames(
                                  styles.tdStatus,
                                  styles.colorRed
                                )}
                              >
                                Выключен
                              </td>
                            )}
                            <td className={styles.tdButtons}>
                              {phones.length === 1 ? (
                                <a
                                  href={`tel:${phones[0]}`}
                                  className={classNames(
                                    styles.btn,
                                    styles.btnSmall,
                                    styles.btnIcon,
                                    styles.phoneBtn
                                  )}
                                  title={phones[0]}
                                >
                                  <i
                                    className={classNames(
                                      styles.icon,
                                      styles.iconPhoneWhite
                                    )}
                                  />
                                </a>
                              ) : phones.length > 1 ? (
                                <div
                                  className={classNames(
                                    styles.dropdownPhone,
                                    styles.dropdown,
                                    styles.dropdownLeft,

                                    styles.iconArrowWhite,
                                    styles.phoneBtn,
                                    phoneDropdownOpenId === id && styles.opened
                                  )}
                                >
                                  <div
                                    onClick={() => togglePhoneDropdown(id)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' || e.key === ' ')
                                        togglePhoneDropdown(id)
                                    }}
                                    className={classNames(
                                      styles.btn,
                                      styles.btnBlue,
                                      styles.btnSmall,
                                      styles.btnIcon,
                                      styles.btnDropdown,
                                      styles.btnDropdownWhite
                                    )}
                                  >
                                    <i
                                      className={classNames(
                                        styles.icon,
                                        styles.iconPhoneWhite
                                      )}
                                    />
                                  </div>
                                  <div
                                    className={classNames(
                                      styles.dropdownContent,
                                      styles.small
                                    )}
                                  >
                                    {phones.map((num, idx) => (
                                      <a
                                        key={`${num}-${idx}`}
                                        href={`tel:${num}`}
                                        className={styles.nowrap}
                                        onClick={() =>
                                          setPhoneDropdownOpenId(null)
                                        }
                                      >
                                        <i
                                          className={classNames(
                                            styles.icon,
                                            styles.icon16,
                                            styles.iconPhone
                                          )}
                                        />{' '}
                                        {num}
                                      </a>
                                    ))}
                                  </div>
                                </div>
                              ) : null}
                              <EditButton
                                onClick={() => handleEditUser(item)}
                              />
                              <DeleteButton
                                onClick={() => handleDeleteUser(item)}
                              />
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Employee
